import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

/*
 * Installable dashboard (PWA). The manifest starts the app at /dashboard; these
 * keep it there and offer the install from inside the dashboard.
 */

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Pages the installed app may show: the dashboard plus what it takes to get into it
// (sign-in, password reset, email verification) and the payment callbacks enrolling lands on.
const APP_PATHS = [
  "/dashboard", "/sign-in", "/login", "/sign-up", "/forgot-password", "/reset-password",
  "/verify-email", "/verify-useremail", "/checkout/callback", "/cohorts/checkout/callback",
  "/payment-complete", "/instructor/accept-invite", "/admin/accept-invite",
];

/** In the installed app, send any marketing/public page back to the dashboard. */
export function StandaloneGuard() {
  const { pathname } = useLocation();
  if (!isStandalone() || APP_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  return <Navigate to="/dashboard" replace />;
}

const DISMISS_KEY = "e1x_install_dismissed";
export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

// Chrome/Edge fire beforeinstallprompt once, usually before the dashboard (or Settings)
// has mounted, so it's caught here at load and shared through useInstall().
let deferredPrompt = null;
let justInstalled = false;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; notify(); });
window.addEventListener("appinstalled", () => { deferredPrompt = null; justInstalled = true; notify(); });

/** { canInstall, installed, ios, install } for the install card and Settings → App. */
export function useInstall() {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    const prompt = deferredPrompt;
    prompt.prompt();
    const choice = await prompt.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") justInstalled = true;
    deferredPrompt = null;
    notify();
  };

  return { canInstall: !!deferredPrompt, installed: isStandalone() || justInstalled, ios: isIos(), install };
}

/**
 * "Install the app" card for the dashboard. Chrome/Edge/Android get the native
 * install prompt; iOS Safari has none, so it shows the Share → Add to Home Screen steps.
 * Once dismissed it stays gone; Settings → App still offers the install.
 */
export function InstallAppPrompt() {
  const { canInstall, installed, ios, install } = useInstall();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
  };

  if (dismissed || installed || (!canInstall && !ios)) return null;

  return (
    <div role="dialog" aria-label="Install the E1X Academy app" style={styles.card}>
      <img src="/pwa-192.png" alt="" width={40} height={40} style={{ borderRadius: 10, flex: "0 0 auto" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ display: "block", fontSize: 14, color: "#0b0f19" }}>Get the E1X Academy app</strong>
        <small style={{ display: "block", fontSize: 12.5, color: "#6b7385", lineHeight: 1.4 }}>
          {ios ? "Tap the Share button, then “Add to Home Screen”." : "Open your classes straight from your home screen."}
          {" "}You can also do this later in Settings → App.
        </small>
      </div>
      {!ios && <button type="button" onClick={install} style={styles.install}>Install</button>}
      <button type="button" onClick={dismiss} aria-label="Dismiss" style={styles.close}>✕</button>
    </div>
  );
}

const styles = {
  card: {
    position: "fixed", left: 16, right: 16, bottom: 16, margin: "0 auto", maxWidth: 420, zIndex: 1000,
    display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#ffffff",
    border: "1px solid #e4e6ec", borderRadius: 16, boxShadow: "0 12px 32px -12px rgba(11,15,25,0.35)",
    fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
  },
  install: {
    background: "#d15424", color: "#ffffff", border: 0, borderRadius: 10, padding: "8px 14px",
    fontWeight: 700, fontSize: 13.5, cursor: "pointer", flex: "0 0 auto",
  },
  close: { background: "none", border: 0, color: "#6b7385", fontSize: 14, cursor: "pointer", padding: 4, flex: "0 0 auto" },
};
